import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Compass, 
  Home, 
  User, 
  Map, 
  Star,
  Unlock,
  Trophy,
  ChevronRight,
  Sun,
  Moon,
  Cloud,
  Droplets,
  Wind,
  Thermometer,
  Calendar,
  MapPin,
  Coffee,
  Utensils,
  Bed,
  Wifi,
  Car,
  Heart,
  Info,
  CheckCircle,
  Circle,
  Lock,
  Sparkles,
  TrendingUp,
  Users,
  MessageCircle,
  Camera
} from 'lucide-react';

interface MasteryLevel {
  level: number;
  title: string;
  progress: number;
  nextUnlock: string;
}

interface DailyInsight {
  icon: React.ReactNode;
  title: string;
  value: string;
  tip?: string;
}

interface LocationSecret {
  id: string;
  name: string;
  type: 'beach' | 'food' | 'view' | 'culture' | 'nature';
  distance: string;
  unlocked: boolean;
  requiredLevel: number;
  description?: string;
}

const MobileMastery: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'discover' | 'journey' | 'connect' | 'master'>('discover');
  const [userType, setUserType] = useState<'guest' | 'operator'>('guest');
  const [masteryLevel, setMasteryLevel] = useState<MasteryLevel>({
    level: 1,
    title: 'Explorer',
    progress: 35,
    nextUnlock: 'Secret Beach Access'
  });
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [weather, setWeather] = useState({ temp: 24, condition: 'sunny' });

  // Simulated real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setMasteryLevel(prev => ({
        ...prev,
        progress: Math.min(100, prev.progress + 0.5)
      }));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const dailyInsights: DailyInsight[] = [
    { icon: <Sun className="w-5 h-5" />, title: 'Sunrise', value: '5:47 AM', tip: 'Perfect for beach yoga' },
    { icon: <Thermometer className="w-5 h-5" />, title: 'Today', value: '24°C', tip: 'Light clothes, evening jacket' },
    { icon: <Droplets className="w-5 h-5" />, title: 'Humidity', value: '65%', tip: 'Comfortable' },
    { icon: <Wind className="w-5 h-5" />, title: 'Wind', value: '12 km/h', tip: 'Great for sailing' }
  ];

  const locationSecrets: LocationSecret[] = [
    { id: '1', name: 'Fisherman\'s Breakfast Spot', type: 'food', distance: '500m', unlocked: true, requiredLevel: 1, description: 'Fresh catch at 6 AM' },
    { id: '2', name: 'Hidden Tide Pools', type: 'nature', distance: '1.2km', unlocked: true, requiredLevel: 1, description: 'Best 2 hours before low tide' },
    { id: '3', name: 'Sunset Rock', type: 'view', distance: '800m', unlocked: masteryLevel.level >= 2, requiredLevel: 2 },
    { id: '4', name: 'Local\'s Beach', type: 'beach', distance: '3km', unlocked: masteryLevel.level >= 3, requiredLevel: 3 },
    { id: '5', name: 'Secret Waterfall', type: 'nature', distance: '5km', unlocked: masteryLevel.level >= 5, requiredLevel: 5 }
  ];

  const getWeatherIcon = () => {
    switch(weather.condition) {
      case 'sunny': return <Sun className="w-8 h-8 text-yellow-400" />;
      case 'cloudy': return <Cloud className="w-8 h-8 text-gray-400" />;
      case 'rainy': return <Droplets className="w-8 h-8 text-blue-400" />;
      default: return <Sun className="w-8 h-8 text-yellow-400" />;
    }
  };

  const renderDiscoverTab = () => (
    <div className="space-y-6">
      {/* Quick Access to Room */}
      <button 
        onClick={() => navigate('/room')}
        className="w-full bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-4 text-white flex items-center justify-between hover:opacity-90 transition-opacity"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Home className="w-6 h-6" />
          </div>
          <div className="text-left">
            <div className="font-bold text-lg">Your Room</div>
            <div className="text-sm opacity-90">Control everything from here</div>
          </div>
        </div>
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Weather & Conditions Card */}
      <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">Perfect Day Ahead</h2>
            <p className="text-sm opacity-90">Ideal for beach & exploration</p>
          </div>
          {getWeatherIcon()}
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          {dailyInsights.map((insight, index) => (
            <div key={index} className="bg-white/20 backdrop-blur rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                {insight.icon}
                <span className="text-xs opacity-90">{insight.title}</span>
              </div>
              <div className="font-bold">{insight.value}</div>
              {insight.tip && (
                <div className="text-xs opacity-80 mt-1">{insight.tip}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Today's Opportunities */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-yellow-400" />
          Today's Opportunities
        </h3>
        
        <div className="space-y-3">
          <div className="bg-gray-700 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Coffee className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Morning Coffee Ritual</div>
                <div className="text-xs text-gray-400">6:30 AM - Rooftop terrace</div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>

          <div className="bg-gray-700 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Map className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Beach Walk Guide Ready</div>
                <div className="text-xs text-gray-400">Low tide at 10 AM - Best time</div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>

          <div className="bg-gray-700 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Local Market Day</div>
                <div className="text-xs text-gray-400">4 PM - Fresh produce & crafts</div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Location Secrets */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-red-400" />
          Location Secrets
        </h3>
        
        <div className="space-y-2">
          {locationSecrets.map(secret => (
            <div 
              key={secret.id} 
              className={`bg-gray-700 rounded-lg p-3 flex items-center justify-between ${!secret.unlocked && 'opacity-50'}`}
            >
              <div className="flex items-center gap-3">
                {secret.unlocked ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <Lock className="w-5 h-5 text-gray-500" />
                )}
                <div>
                  <div className="text-sm font-semibold text-white">
                    {secret.unlocked ? secret.name : `Locked - Level ${secret.requiredLevel} Required`}
                  </div>
                  {secret.unlocked && secret.description && (
                    <div className="text-xs text-gray-400">{secret.description}</div>
                  )}
                  <div className="text-xs text-gray-500">{secret.distance} away</div>
                </div>
              </div>
              {secret.unlocked && (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderJourneyTab = () => (
    <div className="space-y-6">
      {/* Mastery Progress */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">Level {masteryLevel.level}: {masteryLevel.title}</h2>
            <p className="text-sm opacity-90">Next: {masteryLevel.nextUnlock}</p>
          </div>
          <Trophy className="w-8 h-8 text-yellow-300" />
        </div>
        
        <div className="mb-2 flex items-center justify-between text-xs">
          <span>Progress</span>
          <span>{Math.round(masteryLevel.progress)}%</span>
        </div>
        <div className="bg-white/20 rounded-full h-3">
          <div 
            className="bg-white rounded-full h-3 transition-all duration-500"
            style={{ width: `${masteryLevel.progress}%` }}
          />
        </div>
      </div>

      {/* Daily Checklist */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Today's Journey</h3>
        
        <div className="space-y-3">
          {[
            { task: 'Watch sunrise from the beach', done: true, points: 10 },
            { task: 'Try local breakfast special', done: true, points: 15 },
            { task: 'Learn 3 Portuguese phrases', done: false, points: 20 },
            { task: 'Find the hidden tide pool', done: false, points: 25 },
            { task: 'Share a sunset photo', done: false, points: 10 }
          ].map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {item.done ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-500" />
                )}
                <span className={`text-sm ${item.done ? 'text-gray-400 line-through' : 'text-white'}`}>
                  {item.task}
                </span>
              </div>
              <span className="text-xs text-purple-400">+{item.points}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Achievements */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Achievements</h3>
        
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: <Sun className="w-6 h-6" />, name: 'Early Bird', unlocked: true },
            { icon: <Coffee className="w-6 h-6" />, name: 'Coffee Expert', unlocked: true },
            { icon: <Map className="w-6 h-6" />, name: 'Explorer', unlocked: false },
            { icon: <Users className="w-6 h-6" />, name: 'Social', unlocked: false },
            { icon: <Camera className="w-6 h-6" />, name: 'Photographer', unlocked: false },
            { icon: <Heart className="w-6 h-6" />, name: 'Beloved Guest', unlocked: false }
          ].map((achievement, index) => (
            <div 
              key={index}
              className={`bg-gray-700 rounded-lg p-3 flex flex-col items-center justify-center gap-2 ${!achievement.unlocked && 'opacity-40'}`}
            >
              <div className={achievement.unlocked ? 'text-yellow-400' : 'text-gray-500'}>
                {achievement.icon}
              </div>
              <span className="text-xs text-gray-300 text-center">{achievement.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderConnectTab = () => (
    <div className="space-y-6">
      {/* Community Pulse */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl p-6 text-white">
        <h2 className="text-xl font-bold mb-3">Community Pulse</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-white/20 backdrop-blur rounded-lg p-3">
            <div className="font-bold text-lg">12</div>
            <div className="opacity-90">Guests here now</div>
          </div>
          <div className="bg-white/20 backdrop-blur rounded-lg p-3">
            <div className="font-bold text-lg">3</div>
            <div className="opacity-90">Local events today</div>
          </div>
        </div>
      </div>

      {/* Guest Connections */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Fellow Explorers</h3>
        
        <div className="space-y-3">
          <div className="bg-gray-700 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                JS
              </div>
              <div>
                <div className="text-sm font-semibold text-white">John & Sarah</div>
                <div className="text-xs text-gray-400">Love hiking • Room 12</div>
              </div>
            </div>
            <MessageCircle className="w-5 h-5 text-gray-400" />
          </div>

          <div className="bg-gray-700 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                MK
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Marina K.</div>
                <div className="text-xs text-gray-400">Yoga instructor • Room 7</div>
              </div>
            </div>
            <MessageCircle className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Local Connections */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Local Friends</h3>
        
        <div className="space-y-3">
          <div className="bg-gray-700 rounded-lg p-3">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-orange-500 rounded-full" />
              <div>
                <div className="text-sm font-semibold text-white">Carlos - Surf Instructor</div>
                <div className="text-xs text-gray-400">Available tomorrow 7 AM</div>
              </div>
            </div>
            <div className="text-xs text-blue-400">Beginner friendly • Equipment included</div>
          </div>

          <div className="bg-gray-700 rounded-lg p-3">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-green-500 rounded-full" />
              <div>
                <div className="text-sm font-semibold text-white">Maria - Local Chef</div>
                <div className="text-xs text-gray-400">Cooking class Friday 5 PM</div>
              </div>
            </div>
            <div className="text-xs text-blue-400">Traditional dishes • 3 spots left</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMasterTab = () => (
    <div className="space-y-6">
      {/* Mastery Overview */}
      <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
        <h2 className="text-xl font-bold mb-4">Your Mastery Path</h2>
        
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm">Location Knowledge</span>
              <span className="text-sm">78%</span>
            </div>
            <div className="bg-white/20 rounded-full h-2">
              <div className="bg-white rounded-full h-2" style={{ width: '78%' }} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm">Cultural Connection</span>
              <span className="text-sm">65%</span>
            </div>
            <div className="bg-white/20 rounded-full h-2">
              <div className="bg-white rounded-full h-2" style={{ width: '65%' }} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm">Community Integration</span>
              <span className="text-sm">45%</span>
            </div>
            <div className="bg-white/20 rounded-full h-2">
              <div className="bg-white rounded-full h-2" style={{ width: '45%' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Personalized Insights */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Your Patterns</h3>
        
        <div className="space-y-3">
          <div className="bg-gray-700 rounded-lg p-3">
            <div className="text-sm text-gray-400 mb-1">Favorite Time</div>
            <div className="text-white font-semibold">Early mornings (5-7 AM)</div>
            <div className="text-xs text-blue-400 mt-1">Best for: Beach walks, photography</div>
          </div>

          <div className="bg-gray-700 rounded-lg p-3">
            <div className="text-sm text-gray-400 mb-1">Preferred Activities</div>
            <div className="text-white font-semibold">Nature & Relaxation</div>
            <div className="text-xs text-blue-400 mt-1">Try: Hidden waterfall hike</div>
          </div>

          <div className="bg-gray-700 rounded-lg p-3">
            <div className="text-sm text-gray-400 mb-1">Social Style</div>
            <div className="text-white font-semibold">Small groups, deep connections</div>
            <div className="text-xs text-blue-400 mt-1">Join: Sunset meditation circle</div>
          </div>
        </div>
      </div>

      {/* Next Level Unlocks */}
      <div className="bg-gray-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold text-white mb-4">Coming at Level {masteryLevel.level + 1}</h3>
        
        <div className="space-y-3 opacity-75">
          <div className="bg-gray-700 rounded-lg p-3 flex items-center gap-3">
            <Lock className="w-5 h-5 text-gray-500" />
            <div>
              <div className="text-sm font-semibold text-white">Staff Hiking Trail Access</div>
              <div className="text-xs text-gray-400">Secret paths known only to locals</div>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg p-3 flex items-center gap-3">
            <Lock className="w-5 h-5 text-gray-500" />
            <div>
              <div className="text-sm font-semibold text-white">Night Fishing with Locals</div>
              <div className="text-xs text-gray-400">Traditional techniques under stars</div>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg p-3 flex items-center gap-3">
            <Lock className="w-5 h-5 text-gray-500" />
            <div>
              <div className="text-sm font-semibold text-white">Kitchen Access Pass</div>
              <div className="text-xs text-gray-400">Cook your own meals anytime</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      {/* Header */}
      <div className="bg-gray-900/80 backdrop-blur-lg border-b border-gray-800 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Compass className="w-6 h-6 text-blue-400" />
            <div>
              <h1 className="text-lg font-bold text-white">Pousada Mastery</h1>
              <p className="text-xs text-gray-400">Level {masteryLevel.level} • {Math.round(masteryLevel.progress)}%</p>
            </div>
          </div>
          <button
            onClick={() => setUserType(userType === 'guest' ? 'operator' : 'guest')}
            className="text-xs bg-gray-800 text-gray-300 px-3 py-1 rounded-full"
          >
            {userType === 'guest' ? 'Guest View' : 'Operator View'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 pb-20">
        {activeTab === 'discover' && renderDiscoverTab()}
        {activeTab === 'journey' && renderJourneyTab()}
        {activeTab === 'connect' && renderConnectTab()}
        {activeTab === 'master' && renderMasterTab()}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-lg border-t border-gray-800">
        <div className="grid grid-cols-4 py-2">
          {[
            { id: 'discover', icon: Compass, label: 'Discover' },
            { id: 'journey', icon: Map, label: 'Journey' },
            { id: 'connect', icon: Users, label: 'Connect' },
            { id: 'master', icon: Trophy, label: 'Master' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex flex-col items-center gap-1 py-2 ${
                activeTab === tab.id ? 'text-blue-400' : 'text-gray-400'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span className="text-xs">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MobileMastery;